import { Fragment, useId, useState, type ReactNode } from 'react'
import { BookOpen, Building2, Mail, Receipt, Users } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  businessStatusResponse,
  type BusinessDetailResponse,
  type ChangeTenantStatusRequest,
  type DeleteBusinessRequest,
  type ExtendTrialRequest,
} from '@cattle/contracts'

import { useIsPlatformOwner } from '../../../app/session'
import {
  Avatar,
  Button,
  ConfirmDialog,
  DangerZone,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  KeyValue,
  LoadingSkeleton,
  Meter,
  Money,
  PageHeader,
  QueryBoundary,
  Section,
  StatTile,
  StatusBadge,
  TextInput,
  useToast,
  type Column,
  type DangerRow,
  type StatusTone,
} from '../../../components'
import {
  queryKeys,
  useBook,
  useBusiness,
  useBusinessDetail,
  useInvoices,
  usePeople,
  usePlans,
  type Invoice,
  type Person,
} from '../../../fixtures'
import { del, post } from '../../../lib/apiClient'
import { formatBytes, formatDate, formatDateShort } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'
import { routes } from '../../../lib/routes'

/**
 * P-03 · One business.
 *
 * Their invoices and their book sit on one screen, because a support call is
 * about both at once. The danger zone is the platform owner's alone: for a
 * support account it is ABSENT, not disabled — a greyed-out Delete still tells
 * them the power exists.
 */

const COUNT = new Intl.NumberFormat('en-IN')

/** Nodes into a `t()` template — a rupee figure can only be `<Money>`. */
function fill(template: string, nodes: Record<string, ReactNode>): ReactNode {
  return template.split(/(\{\w+\})/g).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1]
    return (
      <Fragment key={index}>{name !== undefined && name in nodes ? nodes[name] : part}</Fragment>
    )
  })
}

/**
 * Opens a draft in the reader's own mail client. It is not a send: no request
 * leaves the console, and the toast must never report a message as delivered.
 */
function openMailDraft(subject: string): void {
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}`
}

const INVOICE_TONES: Record<Invoice['status'], StatusTone> = {
  PAID: 'ok',
  ISSUED: 'warn',
  FAILED: 'danger',
  VOID: 'neutral',
}

/**
 * P8's response shape.
 *
 * `packages/contracts` exports the SCHEMA but not a type beside it — every
 * other payload on this screen has one — so the type is taken off the schema's
 * own `parse` rather than restated here. A restated shape is a second copy of
 * the contract, and the contract is the thing that stops there being two.
 */
type BusinessStatusResponse = ReturnType<typeof businessStatusResponse.parse>

/**
 * What every P8 write needs.
 *
 * The tenant ULID because that is what the route takes, the name because that
 * is what the toast has to say, and the reason because none of these four
 * actions is allowed to happen without one being written to the audit log.
 */
interface StatusArgs {
  id: string
  name: string
  reason: string
}

/** The contract's bounds on a trial extension (`extendTrialRequest`). */
const MIN_EXTEND_DAYS = 1
const MAX_EXTEND_DAYS = 120

const ROLE_TONES: Record<Person['role'], StatusTone> = {
  OWNER: 'ok',
  PARTNER: 'warn',
  STAFF: 'neutral',
}

export function BusinessDetailScreen() {
  const { slug = '' } = useParams<{ slug: string }>()

  const business = useBusiness(slug)
  // The raw P4 payload, for the details panel. Same query key as `useBusiness`,
  // so it is one fetch, not two.
  const detail = useBusinessDetail(slug)
  const invoices = useInvoices(slug)
  const book = useBook(slug)
  const people = usePeople()
  const plans = usePlans()

  const isOwner = useIsPlatformOwner()
  const navigate = useNavigate()
  const toast = useToast()

  /** Opens a draft, says a draft was opened. Nothing is sent from here. */
  const emailOwner = (name: string, ownerName: string) => {
    openMailDraft(t('p.business.emailSubject', { name }))
    toast.show(t('p.business.emailToast', { owner: ownerName }))
  }

  const [suspendOpen, setSuspendOpen] = useState(false)
  const [extendOpen, setExtendOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [extendDays, setExtendDays] = useState('7')
  const reasonId = useId()
  const daysId = useId()

  /**
   * What a status change moves. The detail payload is what this screen draws
   * itself from; the list and the overview both COUNT statuses, so a suspend
   * left uninvalidated keeps a suspended book on the "paying" pill.
   */
  const businessKeys = [
    queryKeys.businessDetail(slug),
    queryKeys.book(slug),
    queryKeys.businesses,
    queryKeys.platformOverview,
    queryKeys.subscriptions,
  ]

  /**
   * The ONE way a paying relationship is enforced against a book.
   *
   * There used to be two, Suspend and Make read-only, because conflict C10 was
   * open and this screen and P-06 argued for different answers. C10 was decided
   * on 10 September 2026: **suspension means read-only, not lockout.** That
   * makes the two controls the same act under two names, so one is offered —
   * and it is the one the rest of the console already says, because the status
   * badge on this page and the filter on P-01 both read "Suspended".
   *
   * `POST …/read-only` still exists in the frozen contract (P8); it is the
   * manual spelling of the state dunning escalates to by itself, and the only
   * thing it does differently is thaw when a payment is recorded. Nothing in
   * this console calls it.
   */
  const suspendBusiness = useApiMutation<StatusArgs, BusinessStatusResponse>({
    run: (args) =>
      post<BusinessStatusResponse>(`/platform/businesses/${args.id}/suspend`, {
        reason: args.reason,
      } satisfies ChangeTenantStatusRequest),
    invalidates: businessKeys,
    success: (_status, args) => t('p.business.suspendToast', { name: args.name }),
  })

  const extendTrial = useApiMutation<StatusArgs & { days: number }, BusinessStatusResponse>({
    run: (args) =>
      post<BusinessStatusResponse>(`/platform/businesses/${args.id}/trial/extend`, {
        days: args.days,
        reason: args.reason,
      } satisfies ExtendTrialRequest),
    invalidates: businessKeys,
    success: (_status, args) => t('p.business.extendedBy', { name: args.name, days: args.days }),
  })

  /**
   * `del` carries a body because the contract asks for the slug typed back —
   * the confirm asks for the NAME, which is what a person can actually recall,
   * and the slug is sent from the row rather than retyped.
   */
  const remove = useApiMutation<StatusArgs & { confirmSlug: string }, BusinessStatusResponse>({
    run: (args) =>
      del<BusinessStatusResponse>(`/platform/businesses/${args.id}`, {
        reason: args.reason,
        confirmSlug: args.confirmSlug,
      } satisfies DeleteBusinessRequest),
    invalidates: businessKeys,
    success: (_status, args) => t('p.business.deleteToast', { name: args.name }),
    // There is nothing left on this route to look at.
    onDone: () => navigate(routes.platform.businesses()),
  })

  /**
   * 1–120 per the contract. Out of range leaves the button off rather than
   * sending a number the server is going to refuse.
   */
  const parsedDays = Number(extendDays.trim())
  const days =
    Number.isInteger(parsedDays) && parsedDays >= MIN_EXTEND_DAYS && parsedDays <= MAX_EXTEND_DAYS
      ? parsedDays
      : null

  if (business.isError && business.error) {
    return <ErrorState error={business.error} onRetry={() => void business.refetch()} />
  }

  if (business.isPending) {
    return (
      <>
        <LoadingSkeleton variant="tiles" />
        <LoadingSkeleton variant="panel" className="mt-3.5" />
      </>
    )
  }

  const data = business.data

  // A resolved query with nothing in it is a wrong slug, not an empty screen.
  if (!data) {
    return (
      <EmptyState
        icon={<Building2 className="size-6" />}
        title={t('p.business.notFound')}
        body={t('p.business.notFoundBody')}
        action={
          <Button onClick={() => navigate(routes.platform.businesses())}>
            {t('p.nav.businesses')}
          </Button>
        }
      />
    )
  }

  const plan = plans.data?.find((entry) => entry.code === data.plan)
  const summary = book.data
  const openBook = () => navigate(routes.platform.businessBook(data.slug))
  const firstName = data.ownerName.split(' ')[0] ?? data.ownerName

  /*
    READ_ONLY is here because dunning produces it on its own and it used to fall
    through to "Payments up to date", which is the one thing it certainly is
    not. It sits beside SUSPENDED rather than under it: since C10 the two mean
    the same to the trader, and the word is all that says who froze the book.
  */
  const payment: { tone: StatusTone; label: string } =
    data.status === 'PAST_DUE'
      ? { tone: 'danger', label: t('p.businesses.statusPastDue') }
      : data.status === 'SUSPENDED'
        ? { tone: 'neutral', label: t('p.businesses.statusSuspended') }
        : data.status === 'READ_ONLY'
          ? { tone: 'neutral', label: t('p.businesses.statusReadOnly') }
          : data.status === 'TRIAL'
            ? { tone: 'warn', label: t('p.plans.free') }
            : { tone: 'ok', label: t('p.business.paymentsUpToDate') }

  const method = invoices.data?.find((invoice) => invoice.method !== null)?.method ?? null

  const invoiceColumns: Column<Invoice>[] = [
    { id: 'date', header: t('label.date'), cell: (row) => formatDateShort(row.date) },
    { id: 'for', header: t('label.for'), cell: (row) => row.forPeriod },
    {
      id: 'how',
      header: t('label.how'),
      cell: (row) =>
        row.method === null ? (
          <span className="text-light">{t('common.none')}</span>
        ) : (
          t(`paymentMethod.${row.method}`)
        ),
    },
    {
      id: 'amount',
      header: t('label.amount'),
      numeric: true,
      cell: (row) =>
        row.amountPaisa === undefined ? (
          <span className="text-light">{t('common.none')}</span>
        ) : (
          <Money paisa={row.amountPaisa} />
        ),
    },
    {
      id: 'status',
      header: t('label.status'),
      numeric: true,
      // Every row here IS an invoice, so every row has one of the four statuses.
      // The old `NONE` branch was the fixture's way of saying "no invoice at
      // all", which a table of invoices can never contain.
      cell: (row) => (
        <StatusBadge tone={INVOICE_TONES[row.status]}>
          {t(`invoiceStatus.${row.status}`)}
        </StatusBadge>
      ),
    },
  ]

  const peopleColumns: Column<Person>[] = [
    { id: 'name', header: t('label.name'), cell: (row) => row.name },
    { id: 'username', header: t('label.username'), cell: (row) => row.username },
    {
      id: 'role',
      header: t('label.role'),
      cell: (row) => (
        <StatusBadge tone={ROLE_TONES[row.role]}>{t(`role.${row.role}`)}</StatusBadge>
      ),
    },
    {
      id: 'lastSignedIn',
      header: t('label.lastSignedIn'),
      numeric: true,
      cell: (row) => row.lastSignedIn,
    },
  ]

  /**
   * Each of these opens on a CLEARED reason. Carrying the sentence typed into
   * the suspend box over into the delete box would file one action's reason
   * against another's audit row.
   */
  /**
   * Suspend and delete no longer demand a typed reason (owner's request, 11 Sept
   * 2026): one press and a confirm. The contract still requires a reason for the
   * audit row, so a blank box files a fixed one — the audit log still records
   * who did it and when, it just says less about why.
   */
  const reasonOr = (fallback: string) => (reason.trim() === '' ? fallback : reason.trim())

  const open = (set: (value: boolean) => void) => () => {
    setReason('')
    set(true)
  }

  const dangerRows: DangerRow[] = [
    {
      id: 'extend',
      label: t('p.business.extendTrial'),
      description: data.trialEndsOn
        ? t('p.business.trialEndsOn', { date: formatDate(data.trialEndsOn) })
        : t('p.business.notOnTrial'),
      action: (
        <Button
          size="sm"
          disabled={!data.trialEndsOn || extendTrial.isPending}
          onClick={open(setExtendOpen)}
        >
          {t('act.extend')}
        </Button>
      ),
    },
    {
      id: 'suspend',
      label: t('p.business.suspend'),
      description: t('p.business.suspendBody'),
      action: (
        <Button
          size="sm"
          variant="danger"
          disabled={suspendBusiness.isPending}
          onClick={open(setSuspendOpen)}
        >
          {t('p.business.suspendAction')}
        </Button>
      ),
    },
    {
      id: 'delete',
      label: t('p.business.deleteAll'),
      description: t('p.business.deleteBody'),
      action: (
        <Button
          size="sm"
          variant="danger"
          disabled={remove.isPending}
          onClick={open(setDeleteOpen)}
        >
          {t('p.business.deleteAction')}
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3.5">
            <Avatar name={data.name} size="lg" />
            {data.name}
          </span>
        }
        subtitle={t('p.business.joined', {
          city: data.city,
          date: formatDate(data.since),
          owner: data.ownerName,
        })}
        actions={
          <>
            {/*
              G22 — the messaging provider — is undecided, and there is no email
              route to call. So this sends NOTHING: it hands the subject to the
              reader's own mail client through `mailto:`, which is the only
              honest version of this button until a provider exists.

              CONTRACT GAP: nothing in the contract carries an email address.
              `businessSummarySchema` has `ownerName` and a nullish `ownerPhone`;
              `personSchema` has a username. The draft therefore opens with the
              To line blank, and the toast says so.
            */}
            <Button
              icon={<Mail className="size-4" />}
              onClick={() => emailOwner(data.name, data.ownerName)}
            >
              {t('act.emailOwner')}
            </Button>
            <Button variant="primary" icon={<BookOpen className="size-4" />} onClick={openBook}>
              {t('p.business.openBook')}
            </Button>
          </>
        }
      />

      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5">
        <StatTile
          surface="heavy"
          label={t('p.business.planTile')}
          value={<span className="text-[22px]">{plan?.name ?? data.plan}</span>}
          sub={
            data.paysPaisa === undefined
              ? t('p.plans.free')
              : fill(t('p.business.planSub'), {
                  amount: <Money paisa={data.paysPaisa} className="text-[11.5px]" />,
                })
          }
        />
        <StatTile
          surface="heavy"
          label={t('label.animals')}
          value={COUNT.format(data.animals)}
          sub={
            plan?.animalLimit == null ? (
              t('p.usage.unlimited')
            ) : (
              <>
                {t('p.business.animalsOf', { limit: plan.animalLimit })}
                <Meter value={data.animals} max={plan.animalLimit} label={t('label.animals')} />
              </>
            )
          }
        />
        <StatTile
          surface="heavy"
          label={t('label.people')}
          value={COUNT.format(people.data?.length ?? 0)}
          sub={
            plan?.peopleLimit == null ? (
              t('p.usage.unlimited')
            ) : (
              <>
                {t('p.business.peopleOf', { limit: plan.peopleLimit })}
                <Meter
                  value={people.data?.length ?? 0}
                  max={plan.peopleLimit}
                  label={t('label.people')}
                />
              </>
            )
          }
        />
        <StatTile
          surface="heavy"
          label={t('label.storage')}
          value={<span className="text-[22px]">{formatBytes(data.storageMb)}</span>}
          sub={t('p.business.storageSub')}
        />
        <StatTile
          surface="heavy"
          label={t('p.business.payments')}
          value={
            <span className="inline-flex text-base">
              <StatusBadge tone={payment.tone}>{payment.label}</StatusBadge>
            </span>
          }
        />
      </div>

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
        <div className="flex flex-col gap-3.5">
          {detail.data ? <BusinessDetailsPanel detail={detail.data} planName={plan?.name} /> : null}

          <Section
            title={t('p.business.invoices')}
            action={<StatusBadge tone={payment.tone}>{payment.label}</StatusBadge>}
          >
            <QueryBoundary
              query={invoices}
              skeleton={<LoadingSkeleton variant="table" rows={3} />}
              isEmpty={(rows) => rows.length === 0}
              empty={
                <EmptyState
                  icon={<Receipt className="size-6" />}
                  title={t('p.business.noInvoicesTitle')}
                  body={t('p.business.noInvoicesBody')}
                />
              }
            >
              {(rows) => (
                <DataTable
                  columns={invoiceColumns}
                  rows={rows}
                  rowKey={(row) => row.id}
                  caption={t('p.business.invoices')}
                />
              )}
            </QueryBoundary>

            <div className="mt-3.5 border-t border-muted/15 pt-1">
              <KeyValue
                label={t('p.business.nextCharge')}
                value={
                  /* `subscription.nextChargeOn` — not the first of next month
                     off the browser's clock, which is what this used to print
                     against a real business's account. */
                  data.subscription?.nextChargeOn == null ? (
                    <span className="text-muted">{t('common.none')}</span>
                  ) : (
                    <span className="inline-flex items-baseline gap-1.5">
                      {formatDate(data.subscription.nextChargeOn)} ·
                      <Money paisa={data.subscription.amountPaisa ?? data.paysPaisa} />
                    </span>
                  )
                }
              />
              <KeyValue
                label={t('p.business.method')}
                value={
                  /* The mask comes from `subscription.paymentMethodMasked`. It
                     used to be the literal string "0300-••••567" — a made-up
                     mobile number printed against a real trader's account. */
                  method === null ? (
                    <span className="text-muted">{t('common.none')}</span>
                  ) : (
                    [t(`paymentMethod.${method}`), data.subscription?.paymentMethodMasked]
                      .filter(Boolean)
                      .join(' ')
                  )
                }
              />
            </div>
          </Section>

          <Section title={t('p.business.peoplePanel')}>
            <QueryBoundary
              query={people}
              skeleton={<LoadingSkeleton variant="table" rows={3} />}
              isEmpty={(rows) => rows.length === 0}
              empty={
                <EmptyState
                  icon={<Users className="size-6" />}
                  title={t('p.business.noPeopleTitle')}
                  body={t('p.business.noPeopleBody')}
                />
              }
            >
              {(rows) => (
                <DataTable
                  columns={peopleColumns}
                  rows={rows}
                  rowKey={(row) => row.id}
                  caption={t('p.business.peoplePanel')}
                />
              )}
            </QueryBoundary>
          </Section>
        </div>

        <div className="flex flex-col gap-3.5">
          <Section
            surface="accent"
            title={t('p.business.theirBook')}
            action={
              <Link
                to={routes.platform.businessBook(data.slug)}
                className="font-display text-xs font-bold text-accent-dark"
              >
                {t('p.business.openIt')}
              </Link>
            }
          >
            {/* `useBook` resolves to `undefined` for a business with no book,
                so the four states are read from the query itself: an absent
                book is an empty state, not a query that never settles. */}
            {book.isError && book.error ? (
              <ErrorState error={book.error} onRetry={() => void book.refetch()} />
            ) : book.isPending ? (
              <LoadingSkeleton variant="panel" />
            ) : !summary ? (
              <EmptyState
                icon={<BookOpen className="size-6" />}
                title={t('p.business.noBookTitle')}
                body={t('p.business.noBookBody')}
              />
            ) : (
              <>
                <KeyValue
                  label={t('p.business.moneyTiedUp')}
                  value={<Money paisa={summary.moneyTiedUpPaisa} short />}
                />
                <KeyValue
                  label={t('p.business.costingThem')}
                  value={
                    <span className="inline-flex items-baseline">
                      <Money paisa={summary.dailyCostPaisa} />
                      <span className="text-muted">{t('unit.perDay')}</span>
                    </span>
                  }
                />
                <KeyValue
                  label={t('p.business.profitBooked')}
                  value={<Money paisa={summary.profitBookedPaisa} signed tone="positive" />}
                />
                <KeyValue
                  label={t('p.business.soldThisSeason')}
                  value={t('p.business.soldOf', {
                    sold: summary.sold,
                    total: summary.sold + summary.inStock,
                  })}
                />
                <KeyValue
                  label={t('p.business.owedToThem')}
                  value={<Money paisa={summary.owedPaisa} tone="warn" />}
                />

                <div className="mt-3.5 border-t border-muted/15 pt-3.5">
                  <Button
                    variant="primary"
                    icon={<BookOpen className="size-4" />}
                    className="w-full"
                    onClick={openBook}
                  >
                    {t('p.business.openNamedBook', { name: firstName })}
                  </Button>
                  <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted">
                    {t('p.business.openLogged')}
                  </p>
                </div>
              </>
            )}
          </Section>

          {/* The four figures P4 actually returns. The deck's placeholders were
              "Last night 02:00", "1.0.4" and "14 minutes ago"; a console that
              keeps printing those is telling support something it does not
              know. App version is not in any payload — see the report — so it
              says so rather than naming a version. */}
          <Section title={t('p.business.health')}>
            <KeyValue
              label={t('p.business.backedUp')}
              value={
                data.health?.lastBackupAt ? (
                  <span className="text-success">
                    {formatDateShort(data.health.lastBackupAt.slice(0, 10))}
                  </span>
                ) : (
                  <span className="text-warn">{t('time.never')}</span>
                )
              }
            />
            <KeyValue
              label={t('p.business.failedUploads')}
              value={COUNT.format(data.health?.failedMedia24h ?? 0)}
            />
            <KeyValue
              label={t('p.business.appVersion')}
              value={<span className="text-muted">{t('common.none')}</span>}
            />
            <KeyValue
              label={t('p.business.lastActivity')}
              value={
                data.health?.lastActivityAt
                  ? formatDateShort(data.health.lastActivityAt.slice(0, 10))
                  : t('time.never')
              }
            />
          </Section>

          {/* Absent for support, not disabled. */}
          {isOwner ? <DangerZone body={t('p.business.dangerBody')} rows={dangerRows} /> : null}
        </div>
      </div>

      {isOwner ? (
        <>
          <ConfirmDialog
            open={extendOpen}
            onClose={() => {
              setExtendOpen(false)
              setReason('')
            }}
            onConfirm={() => {
              if (days === null) return
              extendTrial.mutate({ id: data.id, name: data.name, reason: reason.trim(), days })
            }}
            title={t('p.business.extendTitle', { name: data.name })}
            confirmLabel={t('act.extend')}
            tone="accent"
            disabled={reason.trim() === '' || days === null || extendTrial.isPending}
          >
            <Field
              label={t('p.business.extendDays')}
              hint={t('p.business.extendDaysHint')}
              htmlFor={daysId}
              required
            >
              <TextInput id={daysId} value={extendDays} onChange={setExtendDays} />
            </Field>
            <Field
              label={t('p.business.extendReason')}
              hint={t('p.business.reasonAudited')}
              htmlFor={reasonId}
              required
            >
              <TextInput
                id={reasonId}
                value={reason}
                onChange={setReason}
                placeholder={t('p.inside.reasonPlaceholder')}
              />
            </Field>
          </ConfirmDialog>

          <ConfirmDialog
            open={suspendOpen}
            onClose={() => {
              setSuspendOpen(false)
              setReason('')
            }}
            onConfirm={() =>
              suspendBusiness.mutate({
                id: data.id,
                name: data.name,
                reason: reasonOr(t('p.business.suspendDefaultReason')),
              })
            }
            title={t('p.business.suspendTitle', { name: data.name })}
            confirmLabel={t('p.business.suspendAction')}
            tone="danger"
            consequence={t('p.business.suspendWarning')}
            disabled={suspendBusiness.isPending}
          >
            <Field
              label={t('p.business.suspendReason')}
              hint={t('p.business.reasonOptionalHint')}
              htmlFor={reasonId}
            >
              <TextInput
                id={reasonId}
                value={reason}
                onChange={setReason}
                placeholder={t('p.inside.reasonPlaceholder')}
              />
            </Field>
          </ConfirmDialog>

          <ConfirmDialog
            open={deleteOpen}
            onClose={() => {
              setDeleteOpen(false)
              setReason('')
            }}
            onConfirm={() =>
              remove.mutate({
                id: data.id,
                name: data.name,
                confirmSlug: data.slug,
                reason: reasonOr(t('p.business.deleteDefaultReason')),
              })
            }
            title={t('p.business.deleteTitle', { name: data.name })}
            body={t('p.business.deleteWarning')}
            confirmLabel={t('common.delete')}
            tone="danger"
            consequence={t('confirm.irreversible')}
            disabled={remove.isPending}
          >
            <Field
              label={t('p.business.deleteReason')}
              hint={t('p.business.reasonOptionalHint')}
              htmlFor={reasonId}
            >
              <TextInput
                id={reasonId}
                value={reason}
                onChange={setReason}
                placeholder={t('p.inside.reasonPlaceholder')}
              />
            </Field>
          </ConfirmDialog>
        </>
      ) : null}
    </>
  )
}

/**
 * Everything P4 knows about the business, except the one thing it never
 * carries — a password. The owner asked for it all in one place (11 Sept 2026):
 * the tiles above summarise, this is what support reads out over the phone,
 * starting with the business code the trader types to sign in.
 */
function BusinessDetailsPanel({
  detail,
  planName,
}: {
  detail: BusinessDetailResponse
  planName: string | undefined
}) {
  const { business, subscription, usage } = detail
  const owner = detail.people.find((person) => person.role === 'OWNER')
  const none = <span className="text-muted">{t('common.none')}</span>
  const date = (iso: string | null | undefined) => (iso ? formatDate(iso.slice(0, 10)) : none)

  return (
    <Section title={t('p.business.detailsPanel')}>
      <KeyValue label={t('p.business.signInCode')} value={business.slug} />
      <KeyValue
        label={t('label.status')}
        value={t(`p.business.tenantStatus.${business.status}`)}
      />
      <KeyValue label={t('label.owner')} value={business.ownerName} />
      <KeyValue label={t('p.business.ownerUsername')} value={owner?.username ?? none} />
      <KeyValue label={t('p.businesses.ownerPhoneLabel')} value={business.ownerPhone ?? none} />
      <KeyValue label={t('p.businesses.cityLabel')} value={business.city ?? none} />
      <KeyValue label={t('p.business.joinedOn')} value={date(business.since)} />
      <KeyValue
        label={t('label.plan')}
        value={
          <span>
            {planName ?? business.planCode}
            {' · '}
            {business.paysPaisa === undefined ? (
              t('p.plans.free')
            ) : (
              <Money paisa={business.paysPaisa} />
            )}
          </span>
        }
      />
      <KeyValue label={t('p.business.trialEnds')} value={date(business.trialEndsOn)} />
      <KeyValue
        label={t('p.business.subscriptionStatus')}
        value={
          subscription ? t(`p.business.subscriptionStatus.${subscription.status}`) : none
        }
      />
      <KeyValue
        label={t('p.business.currentPeriod')}
        value={
          subscription
            ? `${formatDate(subscription.currentPeriodStart)} – ${formatDate(subscription.currentPeriodEnd)}`
            : none
        }
      />
      <KeyValue
        label={t('p.business.cancelsAtPeriodEnd')}
        value={subscription ? t(subscription.cancelAtPeriodEnd ? 'label.yes' : 'label.no') : none}
      />
      <KeyValue
        label={t('p.business.bookValue')}
        value={business.bookValuePaisa === undefined ? none : <Money paisa={business.bookValuePaisa} />}
      />
      <KeyValue label={t('p.business.mediaCount')} value={COUNT.format(usage.mediaCount)} />
      <KeyValue label={t('p.business.openTickets')} value={COUNT.format(detail.openTicketCount)} />
    </Section>
  )
}
